import React from 'react';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import {
  type HeroImageV1,
  type RestLCOBanner,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { useCardRenderEnvironment } from '@bbkFunnelComponents/card-render-environment';
import { CloseOfferButton } from '@bbkFunnelComponents/modals/sections';

import './modal.scss';

type Props = {
  modalInfo: RestLCOBanner;
  detailedAction: React.ReactElement;
  close: () => void;
  isPreview: boolean;
};

const CopyContent = styled.div`
  overflow-y: auto;
  align-content: center;
  padding-top: 1rem;
  padding-bottom: 1rem;
  padding-right: 1.5rem;
  padding-left: 1.5rem;
  max-width: 60%;
  max-height: 70px;
  box-sizing: content-box;
`;

const RenderLCOBanner: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      hero,
      info: { details },
    },
  },
  detailedAction,
  close,
  isPreview,
}) => {
  const theme = useTheme() as EmotionTheme;
  const customCardTheme = theme.cardLevelTheme;
  const { previewCanvas } = useCardRenderEnvironment();
  const imgUrl = (hero as HeroImageV1).url;

  const copy = <CustomerHtml value={details} />;

  const customContainerStyle = customCardTheme?.useCustom
    ? {
        backgroundColor: customCardTheme.style.backgroundColor,
        color: customCardTheme.style.textColor,
      }
    : {};

  return (
    <div
      className={cs('banner', 'desktop')}
      style={{
        ...customContainerStyle,
        ...(isPreview
          ? previewCanvas
            ? {
                width: '1440px',
              }
            : {
                width: '1440px',
                transform: 'scale(0.6)',
                transformOrigin: 'top left',
              }
          : { width: '100%' }),
      }}
    >
      <div className="banner-container">
        <img
          data-part="image"
          alt=""
          src={imgUrl}
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            max-width: 150px;
            margin-right: 1.5rem;
          `}
        />
        <CopyContent className="banner-text">
          <div data-part="description">{copy}</div>
        </CopyContent>
        <div
          css={css`
            padding-left: 1rem;
          `}
        >
          {detailedAction}
        </div>
      </div>
      <div
        css={css`
          margin-top: auto;
          margin-bottom: auto;
          display: flex;
          justify-content: center;
        `}
      >
        <CloseOfferButton
          newVariant
          onClick={close}
          css={css`
            margin-right: 2rem;
          `}
        />
      </div>
    </div>
  );
};

export const LCOBanner = Object.assign(RenderLCOBanner, {
  editorHints: {
    description: {
      path: ['requires', 'info', 'details'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    image: {
      path: ['requires', 'hero', 'url'],
      control: 'image',
      selector: '[data-part="image"]',
      placement: 'top-end',
      options: {
        helpLabel: 'Recommended size: 150x70px; < 250kB',
      },
    },
    primaryAction: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="primary-action"]',
      placement: 'top-end',
      label: 'Action',
    },
    otherActions: {
      path: ['requires', 'other_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="other-action"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
